import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StatCard = ({ title, value, icon: Icon, color }) => (
  <Card sx={{
    borderRadius: 3,
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    border: '1px solid #e0e0e0',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)'
    }
  }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Icon sx={{ 
          fontSize: 40, 
          color,
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
        }} />
        <Box sx={{ ml: 2 }}>
          <Typography variant="h6" color="textSecondary" sx={{ 
            fontWeight: 600,
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ 
            fontWeight: '700',
            color: '#4e0159'
          }}>
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_companies: 0,
    total_plans: 0,
    total_company_clients: 0,
    total_employees: 0,
    total_visits: 0,
  });
  const [recentVisits, setRecentVisits] = useState([]);
  const [upcomingVisits, setUpcomingVisits] = useState([]);
  const userRole = localStorage.getItem('role');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleViewHistory = (employeeId) => {
    navigate(`/employees/${employeeId}/history`);
  };

  const fetchDashboardData = async () => {
    try {
      console.log('Fetching dashboard data...');
      const [statsResponse, recentVisitsResponse, upcomingVisitsResponse] = await Promise.all([
        api.get('/v1/dashboard/stats'),
        api.get('/v1/dashboard/recent-visits'),
        api.get('/v1/dashboard/upcoming-visits'),
      ]);
      
      console.log('Dashboard stats response:', statsResponse.data);
      console.log('Recent visits response:', recentVisitsResponse.data);
      console.log('Upcoming visits response:', upcomingVisitsResponse.data);
      
      setStats(statsResponse.data.data);
      setRecentVisits(recentVisitsResponse.data.data || []);
      setUpcomingVisits(upcomingVisitsResponse.data.data || []);
    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      enqueueSnackbar('Error fetching dashboard data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [companiesResponse, employeesResponse, visitsResponse] = await Promise.all([
        axios.get('/api/v1/companies'),
        axios.get('/api/v1/employees'),
        axios.get('/api/v1/visits'),
      ]);

      setStats({
        totalCompanies: companiesResponse.data.data?.length || 0,
        totalEmployees: employeesResponse.data.data?.length || 0,
        totalVisits: visitsResponse.data.data?.length || 0,
        totalRevenue: (visitsResponse.data.data || []).reduce((sum, visit) => sum + (visit.amount || 0), 0),
      });
    } catch (error) {
      toast.error('Error fetching statistics');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 2, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          fontWeight: '700',
          color: '#4e0159',
          letterSpacing: '-0.5px',
          mb: { xs: 2, sm: 3 }
        }}>
          Dashboard
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {userRole == 1 ? (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Companies"
                value={stats.total_companies}
                icon={BusinessIcon}
                color="primary.main"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Plans"
                value={stats.total_plans || 0}
                icon={AttachMoneyIcon}
                color="success.main"
              />
            </Grid>
           
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Company Clients"
                value={stats.total_company_clients}
                icon={BusinessIcon}
            color="#4e0159"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Workers"
                value={stats.total_employees}
                icon={PeopleIcon}
            color="#fdd415"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                title="Total Visits"
                value={stats.total_visits}
                icon={EventIcon}
                color="warning.main"
              />
            </Grid>
          </>
        )}

        {userRole === '2' && (
          <Grid item xs={12}>
            <Paper sx={{ 
              p: 3, 
              width: '100%', 
              overflowX: { xs: 'auto', sm: 'hidden' },
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                boxShadow: '0 16px 64px rgba(0,0,0,0.15)',
                transform: 'translateY(-4px)'
              }
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#4e0159',
                letterSpacing: '-0.5px',
                mb: 3
              }}>
                Recent Visits
              </Typography>
              <TableContainer sx={{ 
                width: '100%',
                margin: 0,
                padding: 0
              }}>
                <Table sx={{
                  width: '100%',
                  margin: 0
                }}>
                  <TableHead
                    sx={{
                      '& .MuiTableCell-head': {
                        backgroundColor: '#4e0159',
                        color: '#ffffff',
                      }
                    }}
                  >
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        backgroundColor: '#4e0159', 
                        color: '#ffffff',
                        borderBottom: 'none',
                        paddingLeft: 2,
                        paddingRight: 1
                      }}>Client</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        backgroundColor: '#4e0159', 
                        color: '#ffffff',
                        borderBottom: 'none',
                        paddingLeft: 1,
                        paddingRight: 1
                      }}>Employee</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        backgroundColor: '#4e0159', 
                        color: '#ffffff',
                        borderBottom: 'none',
                        paddingLeft: 1,
                        paddingRight: 1
                      }}>Date</TableCell>
                      <TableCell sx={{ 
                        fontWeight: 600, 
                        backgroundColor: '#4e0159', 
                        color: '#ffffff',
                        borderBottom: 'none',
                        paddingLeft: 1,
                        paddingRight: 2
                      }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(recentVisits || []).map((visit) => (
                      <TableRow 
                        key={visit.id}
                        sx={{ 
                          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' },
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }
                        }}
                      >
                        <TableCell sx={{ paddingLeft: 2, paddingRight: 1 }}>{visit.clientName}</TableCell>
                        <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{visit.employeeName}</TableCell>
                        <TableCell sx={{ paddingLeft: 1, paddingRight: 1 }}>{formatDate(visit.visit_date)}</TableCell>
                        <TableCell sx={{ paddingLeft: 1, paddingRight: 2 }}>
                          <Chip
                            label={visit.visit_status === 1 ? 'Active' : 'Inactive'}
                            color={visit.visit_status === 1 ? 'success' : 'warning'}
                            size="small"
                            sx={{
                              borderRadius: 2,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default Dashboard; 